import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { TextField as MuiTextField } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, Fragment, useRef } from "react";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import imageCompression from "browser-image-compression";

import {
  TextField,
  LoadingButton,
  CaptchaDisplay,
  Autocomplete,
} from "../../../components";
import { useAxiosV2 } from "../../../hooks";
import CollectionsIcon from "@mui/icons-material/Collections";
import { MdDocumentScanner } from "react-icons/md";
import { HourglassEmpty } from "@mui/icons-material";

const Form = ({
  errorUrl,
  loading,
  captcha,
  ocrLoading,
  setCaptcha,
  formikProps,
  countryCode,
  documentsList,
  setVerification,
  setIdentityType,
}) => {
  const navigate = useNavigate();

  const location = useLocation();
  const inputRef = useRef(null);
  const fileInputRef = useRef(null); // <-- add this
  const cameraInputRef = useRef(null); // <-- add this

  const queryParams = new URLSearchParams(location.search);

  const isMobile = /Mobi|Android/i.test(navigator.userAgent);

  const generateCaptcha = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let randomCaptcha = "";
    for (let i = 0; i < 6; i++) {
      randomCaptcha += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(randomCaptcha);
  };

  const digiLockerID = queryParams.get("id");

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleGoBack = () => {
    // setIdentityType("");
    // setVerification(false);
    digiLockerID ? navigate(errorUrl) : navigate(-1);
  };

  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // encodes file to base64
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  return (
    <Fragment>
      <Fragment>
        <Typography
          variant="h5"
          sx={{ fontWeight: "700", marginBottom: "17.5px" }}
        >
          Enter Passport Card
          <span
            style={{
              color: "red",
              fontWeight: "700",
            }}
          >
            *
          </span>
        </Typography>

        <Grid item container spacing={2}>
          <Grid item xs={4}>
            <Autocomplete
              size="small"
              name="countryCode"
              formikProps={formikProps}
              placeholder="Select countryCode"
              onBlur={formikProps.handleBlur}
              value={
                countryCode.find(
                  (item) => item.code === formikProps.values.countryCode
                ) || null
              }
              onInputChange={(_, value, reason) => {
                if (reason === "clear") {
                  formikProps.setFieldValue("countryCode", "");
                } else {
                  formikProps.setFieldValue("countryCode", value);
                }
              }}
              defaults={{
                primaryKey: "id",
                displayLabel: "name",
              }}
              options={countryCode.map((country) => ({
                id: country.code,
                name: country.name,
              }))}
              error={
                formikProps.touched.countryCode &&
                Boolean(formikProps.errors.countryCode)
              }
            />
          </Grid>

          <Grid item xs={8}>
            <TextField
              size="small"
              fullWidth
              sx={{ marginBottom: "16px" }}
              variant="outlined"
              placeholder="Upload or Capture Passport Card"
              name="passport_Card"
              value={formikProps.values.passport_Card}
              onBlur={formikProps.handleBlur}
              error={
                formikProps.touched.passport_Card &&
                Boolean(formikProps.errors.passport_Card)
              }
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {!ocrLoading ? (
                      <div style={{ display: "flex", gap: "10px" }}>
                        {/* Take Photo Button */}
                        {isMobile && (
                          <Tooltip title="Take Photo">
                            <label>
                              <CameraAltIcon
                                sx={{
                                  color: "#3936EF",
                                  fontSize: "35px",
                                  background: "white",
                                  borderRadius: "7px",
                                  padding: "3px 5px",
                                  cursor: "pointer",
                                }}
                                onClick={() => cameraInputRef.current?.click()}
                              />
                            </label>
                          </Tooltip>
                        )}

                        {/* Upload Button */}
                        <Tooltip title="Upload Document">
                          <label>
                            <CollectionsIcon
                              style={{
                                color: "#3936EF",
                                fontSize: "35px",
                                background: "white",
                                borderRadius: "7px",
                                padding: "3px 5px",
                                cursor: "pointer",
                              }}
                              onClick={() => fileInputRef.current?.click()}
                            />
                          </label>
                        </Tooltip>
                      </div>
                    ) : (
                      <CircularProgress sx={{ color: "#3936EF" }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Hidden Inputs */}
        <MuiTextField
          type="file"
          style={{ display: "none" }}
          inputRef={fileInputRef}
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Compress image
            const options = {
              maxSizeMB: 0.5, // max size in MB
              maxWidthOrHeight: 1024, // max width or height
              useWebWorker: true,
            };
            const compressedFile = await imageCompression(file, options);

            const base64 = await fileToBase64(compressedFile);

            formikProps.setFieldValue("passport_Card", compressedFile.name);
            formikProps.setFieldValue("passportCardData", base64);
          }}
        />

        <MuiTextField
          type="file"
          size="small"
          style={{ display: "none" }}
          inputRef={cameraInputRef}
          accept="image/*"
          capture="environment"
          onChange={async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            // Compress image
            const options = {
              maxSizeMB: 0.5, // max size in MB
              maxWidthOrHeight: 1024, // max width or height
              useWebWorker: true,
            };
            const compressedFile = await imageCompression(file, options);

            const base64 = await fileToBase64(compressedFile);
            formikProps.setFieldValue("passport_Card", compressedFile.name);
            formikProps.setFieldValue("passportCardData", base64);
          }}
        />

        <Grid container>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: "700" }}>
              Enter Captcha
              <span
                style={{
                  color: "red",
                  fontWeight: "700",
                }}
              >
                *
              </span>
            </Typography>
          </Grid>

          <Grid container sx={{ alignSelf: "end", padding: "0px 0px" }}>
            <Grid item xs={7} sx={{ alignSelf: "end" }}>
              <TextField
                // label={<Fragment>Enter Captcha</Fragment>}
                size="small"
                fullWidth
                margin="normal"
                variant="outlined"
                placeholder="Enter Captcha"
                name="captchaInput"
                value={formikProps.values.captchaInput}
                onChange={formikProps.handleChange}
                onBlur={formikProps.handleBlur}
                error={
                  formikProps.touched.captchaInput &&
                  Boolean(formikProps.errors.captchaInput)
                }
                helperText={formikProps.errors.captchaInput}
                inputProps={{
                  maxLength: 6,
                }}
                autoComplete="off"
              />
            </Grid>

            <Grid item xs={5} sx={{ alignSelf: "start", padding: "0px 15px" }}>
              <CaptchaDisplay
                captcha={captcha}
                generateCaptcha={generateCaptcha}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container>
          <LoadingButton
            loading={loading}
            onClick={() => {
              formikProps.handleSubmit();
            }}
            style={{ margin: "10px 0px" }}
            disabled={!formikProps.dirty || !formikProps.isValid}
          >
            Verify Passport
          </LoadingButton>

          <LoadingButton
            onClick={handleGoBack}
            style={{ color: "black" }}
            backgroundColor="#F5F0E5"
          >
            Back
          </LoadingButton>
        </Grid>
      </Fragment>
    </Fragment>
  );
};

export default Form;
